import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { useTranslation } from 'react-i18next';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Badge, 
  ProgressBar, 
  Alert, 
  Nav 
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faExternalLinkAlt, 
  faSyncAlt, 
  faBed, 
  faWalking, 
  faHeartbeat, 
  faCrown, 
  faShieldAlt, 
  faMobileAlt, 
  faChartLine,
  faCloud
} from '@fortawesome/free-solid-svg-icons';

import SafeAdSense from '../shared/SafeAdSense';
import packageJson from '../../../package.json';
import AwsChatbot from '../AwsChatbot';
import defaultAvatar from '../../img_attributions/profile-icon-png-896.png';

/**
 * HealthChartCard
 * Displays synchronized telemetry charts from S3 with automatic retries
 * and an informative fallback visualization if S3 images are unavailable (e.g. 403/404).
 */
const HealthChartCard = ({ metric, period, sanitizedEmail, userEmail }) => {
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [retries, setRetries] = useState(0);

  const metricConfig = {
    sleep: {
      title: 'Sleep Tracking',
      icon: faBed,
      variant: 'info',
      color: '#0dcaf0',
      sampleValue: '7h 48m · 88% Efficiency',
      detail: 'Deep: 1h 42m | REM: 1h 56m | Light: 4h 10m',
      target: 'Target: 8h 00m'
    },
    activity: {
      title: 'Physical Activity',
      icon: faWalking,
      variant: 'warning',
      color: '#f79400',
      sampleValue: '9,450 Steps · 520 kcal',
      detail: 'Active: 62 min | Distance: 6.8 km',
      target: 'Target: 10,000 Steps'
    },
    readiness: {
      title: 'Body Readiness',
      icon: faHeartbeat,
      variant: 'success',
      color: '#198754',
      sampleValue: 'Score: 86 / 100',
      detail: 'Resting HR: 54 bpm | HRV: 62 ms',
      target: 'Optimal Recovery'
    }
  };

  const current = metricConfig[metric] || metricConfig.sleep;
  const imageUrl = `https://healthdash-demo-1.s3.amazonaws.com/${sanitizedEmail}/${period}_${metric}.png`;

  const handleError = () => {
    if (retries < 2) {
      setTimeout(() => {
        setRetries(prev => prev + 1);
      }, 1000 * (retries + 1));
    } else {
      setLoading(false);
      setLoadError(true);
    }
  };

  const handleLoad = () => {
    setLoading(false);
    setLoadError(false);
  };

  return (
    <Card className="h-100 shadow-sm border rounded-3 overflow-hidden">
      <Card.Header className="bg-light d-flex justify-content-between align-items-center py-2 px-3">
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={current.icon} style={{ color: current.color }} />
          <strong className="small text-capitalize">{period} {current.title}</strong>
        </div>
        <Badge bg={current.variant} text={current.variant === 'warning' ? 'dark' : 'white'} className="text-uppercase" style={{ fontSize: '0.65rem' }}>
          {period}
        </Badge>
      </Card.Header>
      <Card.Body className="p-3 text-center d-flex flex-column justify-content-between">
        {!loadError ? (
          <div className="position-relative d-flex justify-content-center align-items-center" style={{ minHeight: '130px' }}>
            {loading && (
              <div className="text-muted small d-flex flex-column align-items-center justify-content-center">
                <FontAwesomeIcon icon={faSyncAlt} spin className="mb-2 text-warning" />
                <span>Checking S3 telemetry...</span>
              </div>
            )}
            <img 
              key={`${imageUrl}-${retries}`}
              src={retries > 0 ? `${imageUrl}?r=${retries}` : imageUrl} 
              alt={`${period} ${metric}`} 
              className={`img-fluid rounded ${loading ? 'd-none' : 'd-block'}`} 
              style={{ maxHeight: '160px', objectFit: 'contain' }}
              onError={handleError}
              onLoad={handleLoad}
            />
          </div>
        ) : (
          <div className="fallback-chart-box py-3 px-2 rounded-3 bg-light border border-light-subtle d-flex flex-column align-items-center justify-content-center">
            <div className="mb-2" style={{ color: current.color, fontSize: '1.8rem' }}>
              <FontAwesomeIcon icon={current.icon} />
            </div>
            <div className="fw-bold text-dark mb-1" style={{ fontSize: '0.95rem' }}>
              {current.sampleValue}
            </div>
            <div className="text-muted small mb-2" style={{ fontSize: '0.78rem' }}>
              {current.detail}
            </div>

            {/* Simulated mini SVG telemetry sparkline */}
            <svg viewBox="0 0 100 24" className="w-75 mb-2" style={{ maxHeight: '28px' }}>
              <polyline
                fill="none"
                stroke={current.color}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                points="0,16 15,14 30,18 45,8 60,12 75,5 90,9 100,7"
              />
            </svg>

            <Badge bg="secondary" className="fw-normal" style={{ fontSize: '0.65rem' }}>
              <FontAwesomeIcon icon={faMobileAlt} className="me-1" />
              Synced via HealthDash Mobile
            </Badge>
          </div>
        )}

        <div className="border-top pt-2 mt-2 d-flex justify-content-between align-items-center text-muted" style={{ fontSize: '0.72rem' }}>
          <span>{current.target}</span>
          <span className="text-success"><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Telemetry Synced</span>
        </div>
      </Card.Body>
    </Card>
  );
};

const ProfilePage = ({ healthCards = [] }) => {
  const { oktaAuth, authState } = useOktaAuth();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [activePeriod, setActivePeriod] = useState('daily');
  const isPro = user?.tier === 'PRO';

  // Sanitize email for S3 path compatibility: email@domain.com -> email-at-domain_com
  const sanitizedEmail = user?.email ? user.email.replace(/@/g, '-at-').replace(/\./g, '_') : 'guest';

  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser().then(setUser);
    }
  }, [authState, oktaAuth]);

  if (!user) return null;

  return (
    <div className="profile-page">
      {/* Standard Hero Header matching Favourites and Home */}
      <header className="justify-content-center py-3 bg-warning text-center text-white shadow-sm mb-4">
        <div className="container">
          <div className="row">
            <div className="col">
              <h1 className="font-weight-bold mb-1">
                {t('header.menu2.title', 'User Profile')} | {authState?.isAuthenticated ? 'The Personalized Cloud Dashboard' : 'The Cloud Dashboard'} {process.env.REACT_APP_VERSION || packageJson.version}
              </h1>
              <p className="mb-0 text-white" style={{ opacity: 0.9 }}>
                Manage your account settings, wearable synchronization, and health telemetry
              </p>
            </div>
          </div>
        </div>
      </header>

      <Container className="profile-container pb-5">
        {/* Pro Appreciation Banner */}
        {isPro && (
          <Alert variant="warning" className="shadow-sm border border-warning mb-4 rounded-3 d-flex align-items-center gap-3">
            <div className="fs-1 text-warning">
              <FontAwesomeIcon icon={faCrown} />
            </div>
            <div>
              <h4 className="alert-heading fw-bold mb-1">Thank you for being a Pro User!</h4>
              <p className="mb-0 text-dark">
                Your 24,99€ monthly contribution directly supports the HealthDash open-source ecosystem.
              </p>
            </div>
          </Alert>
        )}

        {/* User Identity & Health Progress Section */}
        <Row className="g-4 mb-4">
          {/* User Profile Card */}
          <Col lg={5} md={12}>
            <Card className="h-100 shadow-sm border rounded-3">
              <Card.Header className="bg-white border-bottom py-3">
                <div className="d-flex align-items-center gap-3">
                  <img
                    src={user.picture || defaultAvatar}
                    alt={user.name || user.email}
                    className="rounded-circle border border-warning shadow-sm"
                    style={{ width: '64px', height: '64px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultAvatar;
                    }}
                  />
                  <div>
                    <h5 className="fw-bold mb-0 text-dark">{user.name || user.email}</h5>
                    <div className="text-muted small">{user.email}</div>
                    <div className="mt-1 d-flex gap-1 align-items-center">
                      <Badge bg={isPro ? "warning" : "dark"} text={isPro ? "dark" : "white"} className="fw-bold">
                        {user.tier || 'STANDARD'}
                      </Badge>
                      <Badge bg="info" className="text-white">
                        PCD: {packageJson.config?.enablePcdExtensions === 'true' ? 'Enabled (Alpha)' : 'Standard'}
                      </Badge>
                    </div>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-3">
                <div className="ecosystem-summary mb-3">
                  <h6 className="fw-bold text-muted text-uppercase small mb-2">Ecosystem Status (v{packageJson.version})</h6>
                  <ul className="list-unstyled mb-0 small">
                    <li className="mb-2 d-flex justify-content-between align-items-center">
                      <span>Sync Status:</span>
                      <span className="badge bg-success-subtle text-success border border-success px-2 py-1">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Active
                      </span>
                    </li>
                    <li className="mb-2 d-flex justify-content-between align-items-center">
                      <span>Last Feature Used:</span>
                      <strong className="text-dark">{user.lastActivity || 'Dashboard Overview'}</strong>
                    </li>
                    <li className="d-flex justify-content-between align-items-center">
                      <span>Security & Encryption:</span>
                      <span className="badge bg-primary px-2 py-1">
                        <FontAwesomeIcon icon={faShieldAlt} className="me-1" /> TLS 1.3 · IAM Protected
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="cloud-links border-top pt-3">
                  <h6 className="fw-bold text-muted text-uppercase small mb-2">{t('header.menu4.title', 'Supported Dashboards')}</h6>
                  <div className="d-flex flex-column gap-3">
                    <div>
                      <a 
                        href={`https://www.myclouddashboard.healthdash.lv?user=${encodeURIComponent(user.name || user.email)}`} 
                        target="_blank" 
                        rel="noreferrer"
                        className="btn btn-outline-warning btn-sm d-flex justify-content-between align-items-center fw-bold w-100 mb-1"
                      >
                        <span className="d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faHeartbeat} />
                          <span>{t('header.menu5.title', 'HealthDash Dashboard')}</span>
                        </span>
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="small" />
                      </a>
                      <small className="text-muted d-block ps-1" style={{ fontSize: '0.75rem' }}>
                        Pro &amp; Premium full health drilldown (captured from Android App sources). Future: Admin Panel integration.
                      </small>
                    </div>

                    <div>
                      <Link 
                        to={`/monitor?monitor=${encodeURIComponent(user.name || user.email)}`}
                        className="btn btn-outline-dark btn-sm d-flex justify-content-between align-items-center fw-bold w-100 mb-1"
                      >
                        <span className="d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faCloud} />
                          <span>{t('header.menu6.title', 'SystemDash Platform Monitor')}</span>
                        </span>
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="small" />
                      </Link>
                      <small className="text-muted d-block ps-1" style={{ fontSize: '0.75rem' }}>
                        Real-time operational status for Pro provisioned Cloud infrastructure (Monitoring Instance &amp; DSV topology).
                      </small>
                    </div>

                    <div>
                      <Link 
                        to="/ecosystem/SER"
                        className="btn btn-outline-secondary btn-sm d-flex justify-content-between align-items-center fw-bold w-100 mb-1"
                      >
                        <span className="d-flex align-items-center gap-2">
                          <FontAwesomeIcon icon={faSyncAlt} />
                          <span>Synchronization Error Reporting (SER)</span>
                        </span>
                        <FontAwesomeIcon icon={faExternalLinkAlt} className="small" />
                      </Link>
                      <small className="text-muted d-block ps-1" style={{ fontSize: '0.75rem' }}>
                        Troubleshoot missing cards, sync error codes (KB-E-S), wearable auth, and support reporting.
                      </small>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Health Telemetry Progress Bars (Like in Favourites) */}
          <Col lg={7} md={12}>
            <Card className="h-100 shadow-sm border rounded-3">
              <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon icon={faChartLine} className="text-warning" />
                  <strong className="text-dark">Health Telemetry & Goal Progress</strong>
                </div>
                <Badge bg="warning" text="dark" className="fw-bold px-2 py-1">
                  Synchronized
                </Badge>
              </Card.Header>
              <Card.Body className="p-3">
                <p className="text-muted small mb-3">
                  Continuous wearable telemetry metrics synchronized from HealthMe & Samsung Health via CloudDash.
                </p>

                {/* Overall Telemetry Sync Progress */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1 small fw-bold">
                    <span className="text-dark">
                      <FontAwesomeIcon icon={faSyncAlt} className="me-1 text-warning" />
                      Overall Health Data Sync
                    </span>
                    <span className="text-warning">83% Completed</span>
                  </div>
                  <ProgressBar 
                    animated 
                    striped 
                    variant="warning" 
                    now={83} 
                    style={{ height: '14px', borderRadius: '8px' }} 
                  />
                </div>

                {/* Sleep Goal Progress */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1 small fw-bold">
                    <span className="text-dark">
                      <FontAwesomeIcon icon={faBed} className="me-1 text-info" />
                      Sleep Goal (Target: 8 hrs)
                    </span>
                    <span className="text-info">7h 12m (90%)</span>
                  </div>
                  <ProgressBar 
                    variant="info" 
                    now={90} 
                    style={{ height: '10px', borderRadius: '6px' }} 
                  />
                </div>

                {/* Activity & Steps Progress */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1 small fw-bold">
                    <span className="text-dark">
                      <FontAwesomeIcon icon={faWalking} className="me-1 text-warning" />
                      Daily Steps (Target: 10,000)
                    </span>
                    <span className="text-warning">7,840 steps (78%)</span>
                  </div>
                  <ProgressBar 
                    variant="warning" 
                    now={78} 
                    style={{ height: '10px', borderRadius: '6px' }} 
                  />
                </div>

                {/* Readiness Score Progress */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1 small fw-bold">
                    <span className="text-dark">
                      <FontAwesomeIcon icon={faHeartbeat} className="me-1 text-success" />
                      Readiness & Recovery Score
                    </span>
                    <span className="text-success">88 / 100 (Optimal)</span>
                  </div>
                  <ProgressBar 
                    variant="success" 
                    now={88} 
                    style={{ height: '10px', borderRadius: '6px' }} 
                  />
                </div>

                <div className="bg-light p-2 rounded-2 text-muted small d-flex align-items-center justify-content-between">
                  <span>
                    <FontAwesomeIcon icon={faMobileAlt} className="me-1 text-warning" />
                    Last mobile telemetry sync: 12 minutes ago
                  </span>
                  <Badge bg="success" className="fw-normal">Connected</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Personalized Health Insights (Charts) */}
        <Card className="shadow-sm border rounded-3 mb-4">
          <Card.Header className="bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="fw-bold mb-0 text-dark">Personalized Health Insights</h5>
              <small className="text-muted">Visualizing synchronized telemetry for {user.email}</small>
            </div>
            {/* Period Selector Tabs */}
            <Nav 
              variant="pills" 
              activeKey={activePeriod} 
              onSelect={(selectedKey) => selectedKey && setActivePeriod(selectedKey)}
              className="gap-1"
            >
              {['daily', 'weekly', 'monthly', 'yearly'].map(period => (
                <Nav.Item key={period}>
                  <Nav.Link 
                    eventKey={period} 
                    className={`py-1 px-3 text-capitalize fw-bold ${activePeriod === period ? 'bg-warning text-dark shadow-sm' : 'text-muted'}`}
                    style={{ fontSize: '0.85rem' }}
                  >
                    {period}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Card.Header>
          <Card.Body className="p-4">
            <Row className="g-3">
              {['sleep', 'activity', 'readiness'].map(metric => (
                <Col md={4} sm={12} key={`${activePeriod}-${metric}`}>
                  <HealthChartCard 
                    metric={metric} 
                    period={activePeriod} 
                    sanitizedEmail={sanitizedEmail}
                    userEmail={user.email}
                  />
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        {/* Health Cards List */}
        <div className="cards-list mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold mb-0 text-dark">Your Health Cards</h4>
            <Badge bg="dark">{healthCards.length} Cards</Badge>
          </div>
          {healthCards.length > 0 ? (
            <Row className="g-3">
              {healthCards.map(card => (
                <Col md={6} sm={12} key={card.id}>
                  <Card className="h-100 shadow-sm border rounded-3">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center py-2 px-3">
                      <strong className="text-dark">{card.title}</strong>
                      <Badge bg="info" text="dark">
                        {card.meta?.dataSource || 'Android Sync Target'}
                      </Badge>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <p className="mb-2 text-muted">{card.summary}</p>
                      <small className="text-muted d-block border-top pt-2">
                        Processed via <abbr title="Data Source Visualizer">DSV</abbr>
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Card className="p-4 text-center text-muted bg-light border-dashed rounded-3">
              <p className="mb-2">
                No health cards synchronized yet. Please sync from your HealthDash Android app.
              </p>
              <p className="mb-0 small">
                Did mobile sync report success but no cards appear?{' '}
                <Link to="/ecosystem/SER" className="text-decoration-underline fw-bold text-primary">
                  Check Synchronization Error Reporting (SER) for troubleshooting steps &raquo;
                </Link>
              </p>
            </Card>
          )}
        </div>

        {/* Google AdSense Safeguarded Container */}
        <div className="adsense-container mt-4">
          <SafeAdSense
            client={process.env.REACT_APP_GADS_ID || 'ca-pub-2835578352930332'}
            slot="7806394673"
            style={{ display: 'block' }}
            format="auto"
            responsive="true"
          />
        </div>

        {/* AWS Chatbot support for all tiers */}
        <AwsChatbot />
      </Container>
    </div>
  );
};

export default ProfilePage;